// Cliente HTTP da camada de sync — chamadas autenticadas (Bearer swipeuser) aos
// endpoints /v1/swipe-account/{profiles,sync}. Plan: docs/plan/sync-conta-cross-device.md.
//
// Usa getValidSwipeAccessToken() (renova access via refresh se expirou). Toda
// chamada é best-effort do ponto de vista do app: o caller (flusher/puller) trata
// falha sem quebrar a UX (o local-db é a fonte primária).
//
// Todo fetch daqui tem PRAZO por corrida (`syncRequest`, sync/net.ts): antes nenhum
// tinha, e um pedido pendurado segurava o flush (AP-116, sonda AP3). O prazo cai no
// mesmo `catch` da falha de rede.
//
// `asUser` (AP-116, sonda AP3): o token é lido de novo a cada pedido, e a conta da
// sessão pode trocar no meio de uma operação longa (o flush). Com `asUser`, o pedido só
// sai se o token vigente for DAQUELA conta; senão, a chamada devolve a falha dela sem
// tocar a rede. É o que impede o flush da conta A de continuar com o token de B.

import { i18n } from '@app-iptv/i18n'

import { getApiBase } from '../api'
import { isDeadlineError } from '../lib/deadline'

import { monotonicMs, syncRequest } from './net'
import { extractSwipeUserId, getValidSwipeAccessToken } from './swipe-session'
import type { ProfileDTO, PullPage, PushResult, SyncItemDTO, SyncKind } from './types'

/** Opções de um pedido do sync. */
export interface SyncRequestOpts {
  /** Só envia se o token vigente for desta conta (`swipeUserId`). */
  asUser?: string
}

async function authHeaders(asUser?: string): Promise<Record<string, string> | null> {
  const token = await getValidSwipeAccessToken()
  if (!token) return null
  // A conferência é sobre o TOKEN que vai no header, lido agora, e não sobre a sessão
  // lida antes: entre as duas leituras a conta pode ter trocado.
  if (asUser !== undefined && extractSwipeUserId(token) !== asUser) return null
  return { authorization: `Bearer ${token}`, 'content-type': 'application/json' }
}

/** Headers autenticados + Accept-Language (o backend escolhe o idioma do email de
 *  recuperação por este header). Mesma fonte do api.ts (i18n.language). */
async function authHeadersWithLocale(): Promise<Record<string, string> | null> {
  const headers = await authHeaders()
  if (!headers) return null
  return { ...headers, 'accept-language': i18n.language || 'pt-BR' }
}

export interface PushItem {
  kind: SyncKind
  itemKey: string
  payload?: unknown
  updatedAt: string
  deleted?: boolean
}

/** O servidor recusou o push porque o perfil NÃO é da conta da sessão (403
 *  `profile_not_owned`). Tentar de novo com a mesma conta dá o mesmo resultado. */
export interface PushRejected {
  rejected: 'profile_not_owned'
}

/**
 * O servidor recusou o LOTE pelo CONTEÚDO dele (status em `PUSH_TERMINAL_STATUSES`).
 * Repetir o mesmo lote dá a mesma resposta pra sempre; o flusher isola o item culpado
 * e o põe em quarentena (ver "QUARENTENA" em outbox.ts).
 */
export interface PushTerminal {
  terminal: number
  /** O `error` do corpo (`validation_failed`, `payload_too_large`...), ou null. */
  error: string | null
}

/**
 * Os status que são recusa TERMINAL e atribuível ao CONTEÚDO do lote. A lista é
 * fechada de propósito, e o que fica de FORA é a parte que importa:
 *  - 400: `validation_failed` do zod (`SwipeSyncPushBody`: payload > 16 KB, itemKey
 *    vazio/longo, `updatedAt` fora do formato).
 *  - 413: corpo acima do `bodyLimit` (o lote inteiro grande demais; a metade cabe).
 *  - 422: entidade inválida (nenhuma rota de sync usa hoje; é a mesma classe do 400).
 *  Fora, e por quê:
 *  - 401/403: sessão e posse. Têm tratamento próprio (renovar token / pular o grupo
 *    de outra conta) e não dizem nada sobre o item.
 *  - 408/429: tempo e cota. Passam sozinhos: retry no próximo flush.
 *  - 404/405/410 e o resto: dizem respeito à ROTA, não ao item (proxy mal roteado,
 *    deploy no meio). Tratá-los como terminais poria o outbox inteiro em quarentena
 *    num soluço de infraestrutura. Seguem como falha transitória (retry).
 */
export const PUSH_TERMINAL_STATUSES: ReadonlySet<number> = new Set([400, 413, 422])

/**
 * Push em lote de itens de sync. Retorna o resultado do servidor; `PushRejected`
 * quando o perfil não é da conta da sessão; `PushTerminal` quando o servidor recusou
 * o lote pelo conteúdo; ou null se não há sessão Swipe / a chamada falhou por outro
 * motivo (5xx, rede, prazo, 401...: o caller mantém os itens no outbox e tenta depois).
 */
export async function pushSync(
  profileId: string,
  items: PushItem[],
  opts: SyncRequestOpts = {},
): Promise<PushResult | PushRejected | PushTerminal | null> {
  if (items.length === 0) return { applied: 0, ignored: 0 }
  const headers = await authHeaders(opts.asUser)
  if (!headers) return null
  try {
    return await syncRequest(
      `${getApiBase()}/v1/swipe-account/sync`,
      { method: 'POST', headers, body: JSON.stringify({ profileId, items }) },
      'sync/push',
      async (res): Promise<PushResult | PushRejected | PushTerminal | null> => {
        if (!res.ok) {
          if (res.status === 403) {
            const body = (await res.json().catch(() => null)) as { error?: unknown } | null
            if (body?.error === 'profile_not_owned') return { rejected: 'profile_not_owned' }
          }
          if (PUSH_TERMINAL_STATUSES.has(res.status)) {
            const body = (await res.json().catch(() => null)) as { error?: unknown } | null
            const error = typeof body?.error === 'string' ? body.error.slice(0, 64) : null
            return { terminal: res.status, error }
          }
          return null
        }
        return (await res.json()) as PushResult
      },
    )
  } catch {
    return null
  }
}

/** Quanto durou o pedido de uma página do pull (ms), SÓ o pedido: o envio, o corpo e o
 *  parse, sem a espera pelo token (um refresh lento não diz nada sobre a rede da página).
 *  Preenchido em qualquer resposta HTTP (inclusive não 2xx); `null` quando o pedido não
 *  teve resposta (prazo estourado ou rede). */
export interface PullTiming {
  requestMs: number | null
}

/**
 * Pull delta de itens (paginado por cursor). Retorna a página, `'timeout'` se ela não
 * chegou inteira no prazo (o puller reduz a página, ver puller.ts), ou null em outra
 * falha. O caller pagina seguindo `cursor` até null.
 */
export async function pullSync(
  profileId: string,
  opts: { since?: string; cursor?: string; kind?: SyncKind; limit?: number; timing?: PullTiming } = {},
): Promise<PullPage | 'timeout' | null> {
  const headers = await authHeaders()
  if (!headers) return null
  const params = new URLSearchParams({ profileId })
  if (opts.since) params.set('since', opts.since)
  if (opts.cursor) params.set('cursor', opts.cursor)
  if (opts.kind) params.set('kind', opts.kind)
  if (opts.limit) params.set('limit', String(opts.limit))
  try {
    const startedAt = monotonicMs()
    const page = await syncRequest(
      `${getApiBase()}/v1/swipe-account/sync?${params.toString()}`,
      { headers },
      'sync/pull',
      async (res) => (res.ok ? ((await res.json()) as PullPage) : null),
    )
    if (opts.timing) opts.timing.requestMs = monotonicMs() - startedAt
    return page
  } catch (err) {
    return isDeadlineError(err) ? 'timeout' : null
  }
}

/** Lista perfis da conta (delta com `since`, inclui tombstones). null em falha. */
export async function listRemoteProfiles(
  since?: string,
  opts: SyncRequestOpts = {},
): Promise<ProfileDTO[] | null> {
  const headers = await authHeaders(opts.asUser)
  if (!headers) return null
  const qs = since ? `?since=${encodeURIComponent(since)}` : ''
  try {
    return await syncRequest(
      `${getApiBase()}/v1/swipe-account/profiles${qs}`,
      { headers },
      'sync/profiles',
      async (res) => (res.ok ? ((await res.json()) as { profiles: ProfileDTO[] }).profiles : null),
    )
  } catch {
    return null
  }
}

/** Upsert de perfil (LWW). Retorna o perfil resultante ou null em falha. */
export async function upsertRemoteProfile(
  id: string,
  data: {
    name: string
    avatar?: string | null
    kind?: 'adult' | 'kids'
    providerCode?: string | null
    updatedAt?: string
  },
  opts: SyncRequestOpts = {},
): Promise<ProfileDTO | null> {
  const headers = await authHeaders(opts.asUser)
  if (!headers) return null
  try {
    return await syncRequest(
      `${getApiBase()}/v1/swipe-account/profiles/${encodeURIComponent(id)}`,
      { method: 'PUT', headers, body: JSON.stringify(data) },
      'sync/profile-upsert',
      async (res) => (res.ok ? ((await res.json()) as { profile: ProfileDTO }).profile : null),
    )
  } catch {
    return null
  }
}

/** Exclui perfil (tombstone, LWW via updatedAt). Retorna true se marcou. */
export async function deleteRemoteProfile(
  id: string,
  updatedAt?: string,
  opts: SyncRequestOpts = {},
): Promise<boolean> {
  const headers = await authHeaders(opts.asUser)
  if (!headers) return false
  const qs = updatedAt ? `?updatedAt=${encodeURIComponent(updatedAt)}` : ''
  try {
    return await syncRequest(
      `${getApiBase()}/v1/swipe-account/profiles/${encodeURIComponent(id)}${qs}`,
      { method: 'DELETE', headers },
      'sync/profile-delete',
      async (res) => (res.ok ? ((await res.json()) as { deleted: boolean }).deleted : false),
    )
  } catch {
    return false
  }
}

// ===== PIN opcional por perfil (docs/plan/pin-opcional-perfil.md) =====
//
// O PIN plaintext trafega SÓ no corpo destes POSTs autenticados (HTTPS) e NUNCA
// é persistido/logado no cliente. O hash mora só no backend; o cliente recebe
// apenas `hasPin`. Validação (set/verify) é 100% server-side.

export type SetPinResult =
  | { ok: true; hasPin: boolean }
  | { ok: false; error: 'pin_too_weak' | 'pin_invalid_format' | 'not_found' | 'network' }

/** Define/troca o PIN do perfil (4 dígitos). A sessão da conta é a autoridade. */
export async function setProfilePinRemote(profileId: string, pin: string): Promise<SetPinResult> {
  const headers = await authHeaders()
  if (!headers) return { ok: false, error: 'network' }
  try {
    return await syncRequest(
      `${getApiBase()}/v1/swipe-account/profiles/${encodeURIComponent(profileId)}/pin`,
      { method: 'POST', headers, body: JSON.stringify({ pin }) },
      'sync/pin-set',
      async (res): Promise<SetPinResult> => {
        if (res.ok) {
          const body = (await res.json()) as { hasPin: boolean }
          return { ok: true, hasPin: body.hasPin }
        }
        if (res.status === 400) {
          const body = (await res.json().catch(() => null)) as { error?: string } | null
          return {
            ok: false,
            error: body?.error === 'pin_too_weak' ? 'pin_too_weak' : 'pin_invalid_format',
          }
        }
        if (res.status === 404) return { ok: false, error: 'not_found' }
        return { ok: false, error: 'network' }
      },
    )
  } catch {
    return { ok: false, error: 'network' }
  }
}

/** Remove o PIN do perfil (= "esqueci"/desativar). Retorna true se removeu. */
export async function removeProfilePinRemote(profileId: string): Promise<boolean> {
  const headers = await authHeaders()
  if (!headers) return false
  try {
    return await syncRequest(
      `${getApiBase()}/v1/swipe-account/profiles/${encodeURIComponent(profileId)}/pin`,
      { method: 'DELETE', headers },
      'sync/pin-remove',
      async (res) => res.ok,
    )
  } catch {
    return false
  }
}

export type VerifyPinResult =
  | { status: 'ok' }
  | { status: 'incorrect' }
  | { status: 'locked'; retryAfterMs: number }
  | { status: 'no_pin' }
  | { status: 'not_found' }
  | { status: 'network' }

/**
 * Verifica o PIN do perfil. O backend trata rate-limit (429) e lockout (423,
 * 5 falhas/5min). 401 = PIN incorreto. Erro de rede/429 vira 'network' (o caller
 * mostra "tente de novo" sem distinguir). Plaintext nunca persiste no cliente.
 */
export async function verifyProfilePinRemote(profileId: string, pin: string): Promise<VerifyPinResult> {
  const headers = await authHeaders()
  if (!headers) return { status: 'network' }
  try {
    return await syncRequest(
      `${getApiBase()}/v1/swipe-account/profiles/${encodeURIComponent(profileId)}/verify-pin`,
      { method: 'POST', headers, body: JSON.stringify({ pin }) },
      'sync/pin-verify',
      async (res): Promise<VerifyPinResult> => {
        if (res.ok) return { status: 'ok' }
        if (res.status === 401) {
          // 401 do verify = PIN incorreto. (O guard de sessão já passou antes; um 401
          // de sessão inválida é raro aqui e cai em "incorreto", sem vazar nada.)
          const body = (await res.json().catch(() => null)) as { error?: string } | null
          return body?.error === 'pin_incorrect'
            ? { status: 'incorrect' }
            : { status: 'network' }
        }
        if (res.status === 423) {
          const body = (await res.json().catch(() => null)) as { retryAfterMs?: number } | null
          return { status: 'locked', retryAfterMs: body?.retryAfterMs ?? 5 * 60 * 1000 }
        }
        if (res.status === 409) return { status: 'no_pin' }
        if (res.status === 404) return { status: 'not_found' }
        // 429 (rate-limit) e demais → trata como rede (caller pede pra tentar de novo).
        return { status: 'network' }
      },
    )
  } catch {
    return { status: 'network' }
  }
}

// ===== Recuperação do PIN por email (docs/plan/pin-opcional-perfil.md §5) =====
//
// "Esqueci o PIN": o usuário JÁ está logado na conta. O recover dispara um código
// pro email DA CONTA; o confirm com o código REMOVE o PIN do perfil. O código
// nunca é gerado/validado no cliente (tudo server-side). A resposta do recover é
// sempre genérica (não revela se o perfil tem PIN) — anti-enumeração no backend.

/**
 * Solicita o código de recuperação por email (envia pro email da conta). Retorna
 * true se a requisição foi aceita (o backend responde sempre genérico; um false
 * só significa rede/sessão indisponível). Idempotente do ponto de vista da UX.
 */
export async function requestPinRecoverRemote(profileId: string): Promise<boolean> {
  const headers = await authHeadersWithLocale()
  if (!headers) return false
  try {
    return await syncRequest(
      `${getApiBase()}/v1/swipe-account/profiles/${encodeURIComponent(profileId)}/pin/recover`,
      { method: 'POST', headers },
      'sync/pin-recover',
      async (res) => res.ok,
    )
  } catch {
    return false
  }
}

export type ConfirmPinRecoverResult =
  | { status: 'ok' }
  | { status: 'invalid_code' }
  | { status: 'network' }

/**
 * Confirma a recuperação com o código recebido por email. Em sucesso o backend
 * REMOVE o PIN do perfil (o usuário define um novo depois). 400 =
 * código inexistente/expirado/usado. O código nunca é persistido no cliente.
 */
export async function confirmPinRecoverRemote(
  profileId: string,
  code: string,
): Promise<ConfirmPinRecoverResult> {
  const headers = await authHeaders()
  if (!headers) return { status: 'network' }
  try {
    return await syncRequest(
      `${getApiBase()}/v1/swipe-account/profiles/${encodeURIComponent(profileId)}/pin/recover/confirm`,
      { method: 'POST', headers, body: JSON.stringify({ code }) },
      'sync/pin-recover-confirm',
      async (res): Promise<ConfirmPinRecoverResult> => {
        if (res.ok) return { status: 'ok' }
        if (res.status === 400) return { status: 'invalid_code' }
        return { status: 'network' }
      },
    )
  } catch {
    return { status: 'network' }
  }
}

export type { SyncItemDTO }
